"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    }
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) if (e.indexOf(p[i]) < 0)
            t[p[i]] = s[p[i]];
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
var classNames = require("classnames");
var PropTypes = require("prop-types");
var React = require("react");
var react_1 = require("react");
var propTypeUtils_1 = require("../../utils/propTypeUtils");
var Components_1 = require("../../utils/Components");
var nodash_1 = require("../../utils/nodash");
/**
 * Breakpoint for small screens in JavaScript is needed to remove the colSpan
 * properties on small screens, since that is not possible with pure CSS.
 */
var style_1 = require("../../utils/style");
var ExpandAnimationComponent_1 = require("../Internal/ExpandAnimationComponent");
var ChevronDownIcon_1 = require("../Icons/ChevronDownIcon");
/**
 * Number of milliseconds to wait between handling of scroll-events.
 */
var scrollThrottleWait = 100;
var propTypes = {
    children: propTypeUtils_1.allowedChildren('thead', 'tbody'),
    variant: PropTypes.oneOf(['rich', 'reduced']),
    onColumnClick: PropTypes.func,
    onVisibleColumnChange: PropTypes.func,
    selectedColumn: PropTypes.number
};
/**
 * The `ComparisonTable` component visualizes a tariff comparison table and can be
 * used in the `reduced` and the `rich` variant.
 *
 * The table should be specified by a table head (`thead`) and a table body (`tbody`).
 * At least two columns must be defined: The first column should describe
 * the tariff details that will be compared. The second and subsequent columns the specific
 * values for each compared tariff.
 *
 * Only one tariff will be displayed on small screens and the user can switch
 * to other tariffs via tabs or another mechanism (e.g. `Radio`-elements) provided by the
 * surrounding application.
 *
 * For large tables (`tbody` bigger than `thead`), the table head of a `rich` table is sticky
 * and will be visible even upon scrolling.
 *
 * Example:
 *
 * ```html
 * <ComparisonTable>
 *     <thead>
 *         <tr>
 *             <th />
 *             <th>Tariff A</th>
 *             <th>Tariff B</th>
 *         </tr>
 *         <tr>
 *             <th scope="row">Premium</th>
 *             <td><Price value={1.23}/></td>
 *             <td><Price value={3.45}/></td>
 *         </tr>
 *     </thead>
 *     <tbody>
 *         <tr>
 *             <th scope="row">Insurance sum</th>
 *             <td>10 EUR</td>
 *             <td>20 EUR</td>
 *         </tr>
 *     </tbody>
 *     <tbody>
 *         <tr>
 *             <th scope="rowgroup" colSpan={3}>Additional options</th>
 *         </tr>
 *         <tr>
 *             <th scope="row">Chief physician</th>
 *             <td>yes</td>
 *             <td>no</td>
 *         </tr>
 *     </tbody>
 * </ComparisonTable>
 * ```
 *
 * On small screens a table head `<thead>` element must exist and the navigation tab content
 * is defined by the first `<th>` element content of the first `<tr>` element.
 *
 * Other props are passed through to the `table` element.
 */
var ComparisonTableWithinContext = /** @class */ (function (_super) {
    __extends(ComparisonTableWithinContext, _super);
    function ComparisonTableWithinContext(props) {
        var _this = _super.call(this, props) || this;
        _this.updateLayout = function () {
            _this.updateSmallScreenState();
            // re-attach header to table to update bounds and reattach
            // in the next tick if necessary
            _this.setState({ isHeaderSticky: false });
            setTimeout(function () {
                _this.updateStickyHeader();
            }, 0);
        };
        _this.state = {
            isSmallScreen: false,
            visibleColumn: props.selectedColumn || 1,
            isHeaderSticky: false,
            collapsibleIsOpen: {}
        };
        _this.bodyIdPrefix = props.ergoElements.idGenerator.next();
        return _this;
    }
    ComparisonTableWithinContext.prototype.componentDidUpdate = function (_a) {
        var prevSelectedColumn = _a.selectedColumn;
        if (this.props.selectedColumn !== prevSelectedColumn) {
            this.setState({
                visibleColumn: this.props.selectedColumn || 1
            });
        }
    };
    ComparisonTableWithinContext.prototype.updateSmallScreenState = function () {
        this.setState({ isSmallScreen: window.innerWidth < style_1.largeScreenMinWidth });
    };
    ComparisonTableWithinContext.prototype.getBoundingRects = function () {
        return {
            rootElement: this.rootElement.getBoundingClientRect(),
            tableHead: this.tableHeadElement.getBoundingClientRect(),
            tabs: this.smallScreenTabsElement &&
                this.columnCount > 2 &&
                this.state.isSmallScreen
                ? this.smallScreenTabsElement.getBoundingClientRect()
                : { height: 0 }
        };
    };
    ComparisonTableWithinContext.prototype.updateStickyHeader = function () {
        if (!this.rootElement ||
            !this.tableHeadElement ||
            this.props.variant !== 'rich') {
            return;
        }
        var boundingRects = this.getBoundingRects();
        var isHeaderSticky = boundingRects.rootElement.top < boundingRects.tabs.height &&
            boundingRects.rootElement.bottom >
                boundingRects.tableHead.height + boundingRects.tabs.height &&
            boundingRects.tableHead.height + boundingRects.tabs.height <
                boundingRects.rootElement.height * 0.5;
        this.setState({ isHeaderSticky: isHeaderSticky });
    };
    ComparisonTableWithinContext.prototype.setVisibleColumn = function (column) {
        this.setState({ visibleColumn: column });
        if (typeof this.props.onVisibleColumnChange === 'function') {
            this.props.onVisibleColumnChange(column);
        }
    };
    ComparisonTableWithinContext.prototype.componentDidMount = function () {
        this.updateStickyHeaderThrottled = nodash_1.throttle(this.updateStickyHeader.bind(this), scrollThrottleWait, true);
        // FIXME: a better way is to use window.matchMedia().addListener
        // but I (mareuter) couldn't make this work in the tests
        // matchMedia is better, because the handler is only called when the
        // width actually changes from one side of the breakpoint to the other
        window.addEventListener('resize', this.updateLayout);
        window.addEventListener('scroll', this.updateStickyHeaderThrottled);
        this.updateSmallScreenState();
        this.updateStickyHeader();
    };
    ComparisonTableWithinContext.prototype.componentWillUnmount = function () {
        window.removeEventListener('resize', this.updateLayout);
        window.removeEventListener('scroll', this.updateStickyHeaderThrottled);
    };
    ComparisonTableWithinContext.prototype.renderStickyTableHead = function (children, smallScreenTabs) {
        var _this = this;
        var _a = this.tableHeadElement.getBoundingClientRect(), height = _a.height, width = _a.width;
        var tableHead = children
            .filter(function (child) { return child.type === 'thead'; })
            .map(function (thead) { return [
            React.createElement("table", { key: "stickyhead", style: { width: width }, className: classNames('ee_comparison-table') }, _this.renderTableHead(thead))
        ]; })[0]; // use only first element because a table can't have more than one thead-child
        var tabNavigation = this.columnCount > 2 && this.state.isSmallScreen
            ? smallScreenTabs
            : undefined;
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: 'ee_comparison-table--sticky' },
                tabNavigation,
                tableHead),
            React.createElement("div", { key: "spacer", style: { height: height } })));
    };
    ComparisonTableWithinContext.prototype.renderTableHead = function (thead) {
        var _this = this;
        var isReduced = this.props.variant === 'reduced';
        var isRich = this.props.variant === 'rich';
        var highlightSelectedHeader = this.props.highlightSelectedHeader;
        return React.cloneElement(thead, {
            key: 'thead',
            ref: function (el) {
                _this.tableHeadElement = el;
            },
            children: React.Children.map(React.Children.toArray(thead.props.children), function (tr, index) {
                if (index === 0) {
                    _this.columnCount = Array.isArray(tr.props.children)
                        ? tr.props.children.length
                        : 1;
                }
                var selectedColumn = typeof _this.props.selectedColumn === 'number'
                    ? _this.props.selectedColumn
                    : -1;
                return React.cloneElement(tr, {
                    className: classNames(tr.props.className, 'ee_comparison-table__head-row'),
                    children: React.Children.map(React.Children.toArray(tr.props.children), function (cell, thIndex) {
                        return React.cloneElement(cell, {
                            className: classNames(cell.props.className, 'ee_comparison-table__head-cell', {
                                'ee_comparison-table__tariff-title-row': index === 0 || isReduced,
                                'ee_comparison-table__tariff-title-row--rich': index === 0 && isRich,
                                'ee_comparison-table__tariff-title-row--reduced': isReduced,
                                'ee_comparison-table__tariff-title-row--invisible': index === 0 && thIndex === 0 && isRich,
                                'ee_comparison-table__tariff-title-row--selected': thIndex === selectedColumn && index === 0,
                                'ee_comparison-table__tariff-pricing-row': index === 1 && isRich,
                                'ee_comparison-table__tariff-pricing-row--first-column-hidden-mobile': highlightSelectedHeader && thIndex === 0,
                                'ee_comparison-table__tariff-pricing-row--selected': index === 1 &&
                                    isRich &&
                                    highlightSelectedHeader &&
                                    thIndex === selectedColumn,
                                'ee_comparison-table__head-cell--clickable': typeof _this.props.onColumnClick === 'function' &&
                                    thIndex > 0,
                                'ee_comparison-table__head-cell--label': index !== 0 && thIndex === 0,
                                'ee_comparison-table__column-hidden': thIndex > 0 && thIndex !== _this.state.visibleColumn,
                                'ee_comparison-table__head-cell--data': index > 1 && thIndex !== 0
                            }),
                            // using bind to ensure proper value of current column
                            // calling it via anonymous function won't always have the current value
                            onClick: _this.props.onColumnClick && thIndex > 0
                                ? _this.props.onColumnClick.bind(_this, thIndex)
                                : undefined,
                            colSpan: highlightSelectedHeader && _this.state.isSmallScreen && 2
                        });
                    })
                });
            })
        });
    };
    ComparisonTableWithinContext.prototype.renderTableBodyRows = function (tbody, isVisible, isCollapsible) {
        var _this = this;
        var childrenArray = React.Children.toArray(tbody.props.children);
        return React.Children.map(childrenArray, function (tr, trIndex) {
            var columnOffset = 0;
            var firstColumnVisible = false;
            firstColumnVisible = tr.props['data-role'] === 'subhead';
            React.Children.forEach(React.Children.toArray(tr.props.children), function (cell, tdIndex) {
                // first column spans across all columns
                if (tdIndex === 0 &&
                    Number(cell.props.colSpan) === _this.columnCount) {
                    firstColumnVisible = true;
                }
                // currently visible column exists as single cell and has content
                if (tdIndex > 0) {
                    if (tdIndex + columnOffset === _this.state.visibleColumn &&
                        (!cell.props.colSpan || cell.props.colSpan <= 1) &&
                        cell.props.children) {
                        firstColumnVisible = true;
                    }
                }
                if (cell.props.colSpan > 1) {
                    columnOffset += Number(cell.props.colSpan) - 1;
                }
            });
            columnOffset = 0;
            var selectedColumn = typeof _this.props.selectedColumn === 'number'
                ? _this.props.selectedColumn
                : -1;
            return React.cloneElement(tr, {
                className: classNames(tr.props.className, 'ee_comparison-table__body-row', {
                    'ee_comparison-table__body-row--before-spacer': isCollapsible && trIndex === childrenArray.length - 1
                }),
                children: React.Children.map(React.Children.toArray(tr.props.children), function (td, tdIndex) {
                    var contentDivClassName = classNames('ee_comparison-table__body-cell-content', {
                        'ee_comparison-table__body-cell-content--label': tdIndex === 0,
                        'ee_comparison-table__subhead': Number(td.props.colSpan) === _this.columnCount
                    });
                    var clonedElement = React.cloneElement(td, {
                        className: classNames(td.props.className, 'ee_comparison-table__body-cell', {
                            'ee_comparison-table__body-cell--clickable': typeof _this.props.onColumnClick === 'function' &&
                                tdIndex > 0,
                            'ee_comparison-table__tariff-column--selected': tdIndex + columnOffset === selectedColumn &&
                                (!td.props.colSpan || td.props.colSpan <= 1),
                            'ee_comparison-table__tariff-column': tdIndex + columnOffset > 0,
                            'ee_comparison-table__column-hidden': (tdIndex > 0 &&
                                tdIndex + columnOffset !== _this.state.visibleColumn) ||
                                !firstColumnVisible,
                            'ee_comparison-table__body-cell--data': tdIndex !== 0
                        }),
                        // tslint:disable-next-line:max-line-length
                        colSpan: _this.state.isSmallScreen
                            ? Number(td.props.colSpan) === _this.columnCount
                                ? 2
                                : undefined
                            : td.props.colSpan,
                        // using bind to ensure proper value of current column
                        // calling it via anonymous function won't always have the current value
                        onClick: _this.props.onColumnClick && tdIndex > 0
                            ? _this.props.onColumnClick.bind(_this, tdIndex + columnOffset)
                            : undefined,
                        children: isCollapsible ? (React.createElement(ExpandAnimationComponent_1.default, { show: isVisible },
                            React.createElement("div", { className: contentDivClassName }, td.props.children))) : (React.createElement("div", null,
                            React.createElement("div", { className: contentDivClassName }, td.props.children)))
                    });
                    if (td.props.colSpan > 1) {
                        columnOffset += Number(td.props.colSpan) - 1;
                    }
                    return clonedElement;
                })
            });
        });
    };
    ComparisonTableWithinContext.prototype.renderCollapsibleToggle = function (columnCount, onToggle, isVisible, openLabel, closedLabel, bodyId) {
        return (React.createElement("tbody", { className: "ee_comparison-table__body" },
            React.createElement("tr", { className: "ee_comparison-table__collapsible-toggle-row" },
                React.createElement("th", { className: "ee_comparison-table__head-cell", colSpan: columnCount },
                    React.createElement("button", { onClick: onToggle, className: "ee_comparison-table__collapsible-toggle", type: "button", "aria-expanded": isVisible, "aria-controls": bodyId },
                        React.createElement(ChevronDownIcon_1.ChevronDownIcon, { className: classNames('ee_comparison-table__collapsible-icon', {
                                'ee_comparison-table__collapsible-icon--open': isVisible
                            }) }),
                        ' ',
                        isVisible ? openLabel || 'Schließen' : closedLabel || 'Öffnen')))));
    };
    ComparisonTableWithinContext.prototype.renderCollapsibleSpacer = function (index, columnCount, isLast) {
        return (React.createElement("tr", { key: "tbody-spacer-" + index, "aria-hidden": "true", className: "ee_comparison-table__collapsible-spacer" },
            React.createElement("td", { colSpan: columnCount },
                React.createElement("div", { className: classNames('ee_comparison-table__collapsible-spacer-content', {
                        'ee_comparison-table_collapsible-spacer-content--last': isLast
                    }) }))));
    };
    ComparisonTableWithinContext.prototype.renderTableBody = function (tbody, index, bodyAmount) {
        var _this = this;
        var isCollapsible = tbody.props['data-collapsible'] === 'true';
        var isVisible = Boolean(this.state.collapsibleIsOpen[index]) ||
            (this.state.collapsibleIsOpen[index] === undefined &&
                tbody.props['data-initially-open'] === 'true') ||
            !isCollapsible;
        var bodyId = this.bodyIdPrefix + "-" + index;
        var fullWidthColumnCount = this.state.isSmallScreen
            ? 2
            : this.columnCount;
        return (React.createElement(React.Fragment, { key: "tbody-" + index },
            isCollapsible &&
                this.renderCollapsibleToggle(fullWidthColumnCount, function () {
                    _this.setState(function (prevState) {
                        var _a;
                        return ({
                            collapsibleIsOpen: __assign({}, prevState.collapsibleIsOpen, (_a = {}, _a[index] = !isVisible, _a))
                        });
                    });
                }, isVisible, tbody.props['data-open-label'], tbody.props['data-closed-label'], bodyId),
            React.cloneElement(tbody, {
                className: classNames(tbody.props.className, 'ee_comparison-table__body', {
                    'ee_comparison-table__collapsible': isCollapsible,
                    'ee_comparison-table__collapsible--closed': isCollapsible && !isVisible
                }),
                id: bodyId,
                'aria-hidden': !isVisible,
                children: isCollapsible
                    ? this.renderTableBodyRows(tbody, isVisible, isCollapsible).concat([
                        this.renderCollapsibleSpacer(index, fullWidthColumnCount, index === bodyAmount - 1)
                    ]) : this.renderTableBodyRows(tbody, isVisible, isCollapsible)
            })));
    };
    ComparisonTableWithinContext.prototype.renderTableChildren = function (children) {
        var _this = this;
        var childElements = Components_1.getChildElements(children);
        return childElements.map(function (child, index) {
            if (child.type === 'thead') {
                return _this.state.isHeaderSticky ? null : _this.renderTableHead(child);
            }
            else {
                return _this.renderTableBody(child, index, childElements.length);
            }
        });
    };
    ComparisonTableWithinContext.prototype.renderSmallScreenTabs = function () {
        var _this = this;
        var columnTitles = this.getColumnTitles();
        return (React.createElement("div", { className: 'ee_comparison-table__small-screen-tabs', ref: function (e) {
                _this.smallScreenTabsElement = e;
            } }, columnTitles.map(function (title, index) { return (React.createElement("button", { type: "button", key: "small-screen-tab-" + index, className: classNames('ee_comparison-table__small-screen-tab', {
                'ee_comparison-table__small-screen-tab--active': index + 1 === _this.state.visibleColumn
            }, {
                'ee_comparison-table__small-screen-tab--with-border': index + 1 === _this.state.visibleColumn &&
                    _this.props.highlightSelectedHeader
            }), onClick: function () {
                _this.setVisibleColumn(index + 1);
            } }, title)); })));
    };
    ComparisonTableWithinContext.prototype.getColumnTitles = function () {
        // get the first thead element
        var thead = Components_1.getChildElementsOfType(this.props.children, 'thead')[0];
        if (!thead) {
            throw new Error('ComparisonTable requires a <thead> child');
        }
        // get the first tr element within the thead
        var firstTr = Components_1.getChildElementsOfType(thead.props.children, 'tr')[0];
        if (!firstTr) {
            throw new Error('ComparisonTable requires a <tr> within a <thead> child');
        }
        // get all th elements within the first tr
        var ths = Components_1.getChildElementsOfType(firstTr.props.children, 'th');
        if (ths.length === 0) {
            throw new Error('ComparisonTable requires <th> elements within the first row in the <thead> child');
        }
        return ths
            .filter(function (_th, index) { return index > 0; })
            .map(function (th) { return th.props['data-short-description'] || th.props.children; });
    };
    ComparisonTableWithinContext.prototype.render = function () {
        var _this = this;
        var _a = this.props, children = _a.children, variant = _a.variant, className = _a.className, highlightSelectedHeader = _a.highlightSelectedHeader, selectedColumn = _a.selectedColumn, onColumnClick = _a.onColumnClick, onVisibleColumnChange = _a.onVisibleColumnChange, ergoElements = _a.ergoElements, other = __rest(_a, ["children", "variant", "className", "highlightSelectedHeader", "selectedColumn", "onColumnClick", "onVisibleColumnChange", "ergoElements"]);
        var table = this.renderTableChildren(children);
        var smallScreenTabs = variant === 'rich' && this.renderSmallScreenTabs();
        var tableClassName = classNames('ee_comparison-table', className, {
            'ee_comparison-table--unselected': variant === 'reduced' && selectedColumn === undefined
        });
        return (React.createElement("div", null,
            smallScreenTabs,
            React.createElement("div", { ref: function (el) {
                    _this.rootElement = el;
                } },
                this.state.isHeaderSticky &&
                    this.renderStickyTableHead(children, smallScreenTabs),
                React.createElement("table", __assign({ className: tableClassName }, other), table))));
    };
    ComparisonTableWithinContext.propTypes = propTypes;
    ComparisonTableWithinContext.defaultProps = {
        children: null,
        onColumnClick: undefined,
        selectedColumn: undefined,
        onVisibleColumnChange: undefined,
        variant: 'rich'
    };
    return ComparisonTableWithinContext;
}(react_1.PureComponent));
exports.ComparisonTableWithinContext = ComparisonTableWithinContext;
//# sourceMappingURL=ComparisonTableWithinContext.js.map
